import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  FileText, Loader2, Save, Edit2, Check, X, ExternalLink 
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContentManagement = ({ token }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/site-content/admin/all-pages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPages(res.data);
    } catch (err) {
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (page) => {
    setEditModal(page);
    setEditContent(page.content || '');
  };

  const handleSave = async () => {
    if (!editModal) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/site-content/page/${editModal.slug}`,
        { content: editContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Content for "${editModal.label}" updated successfully`);
      setEditModal(null);
      fetchPages();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  // Group pages by section
  const groupedPages = pages.reduce((acc, page) => {
    const section = page.section_title || page.section || 'Other';
    if (!acc[section]) acc[section] = [];
    acc[section].push(page);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Content Management</h2>
          <p className="text-sm text-gray-500 mt-1">
            Edit content for footer links and informational pages
          </p>
        </div>
      </div>

      {Object.entries(groupedPages).map(([section, sectionPages]) => (
        <div key={section} className="bg-white rounded-xl border overflow-hidden">
          <div className="bg-gray-50 px-5 py-3 border-b">
            <h3 className="font-medium text-gray-900 capitalize">{section}</h3>
          </div>
          <div className="divide-y">
            {sectionPages.map((page) => (
              <div 
                key={page.slug} 
                className="px-5 py-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center gap-3">
                  <FileText className={`w-5 h-5 ${page.has_content ? 'text-green-500' : 'text-gray-300'}`} />
                  <div>
                    <p className="font-medium text-gray-900">{page.label}</p>
                    <p className="text-xs text-gray-500">/page/{page.slug}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {page.has_content ? (
                    <span className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" />
                      Has content
                    </span>
                  ) : (
                    <span className="text-xs bg-yellow-50 text-yellow-700 px-2 py-1 rounded-full flex items-center gap-1">
                      <X className="w-3 h-3" />
                      No content
                    </span>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(page)}
                    className="text-xs"
                  >
                    <Edit2 className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                  <a
                    href={`/page/${page.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {pages.length === 0 && (
        <div className="bg-white rounded-xl border p-12 text-center">
          <FileText className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No editable pages found</p>
        </div>
      )}

      {/* Edit Modal */}
      <Dialog open={!!editModal} onOpenChange={() => setEditModal(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogTitle>Edit Content: {editModal?.label}</DialogTitle>
          <DialogDescription>
            Update the content for this page. This will be visible to all users who visit /page/{editModal?.slug}
          </DialogDescription>
          
          <div className="mt-4">
            <label className="text-sm font-medium text-gray-700 mb-2 block">
              Page Content
            </label>
            <Textarea
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              placeholder="Enter the content for this page..."
              rows={12}
              className="font-mono text-sm"
            />
            <p className="text-xs text-gray-500 mt-2">
              Use double line breaks to create paragraphs. Basic text formatting is supported.
            </p>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => setEditModal(null)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#D65A31] hover:bg-[#c04d28]"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Content
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentManagement;
